import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Tab,
  Theme,
  Tooltip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import { AxiosRequestConfig } from 'axios';
import clsx from 'clsx';
import { FieldArray, Form, Formik } from 'formik';
import get from 'lodash/get';
import { Duration } from 'luxon';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { themeModeAtom, themeModeEnum } from 'src/recoil/atoms/themeModeAtom';
import * as Yup from 'yup';
import Axios from '../../common/axiosInterceptor';
import { LabelledOutline } from '../../common/final-form-material-ui';
import { FormikTextField } from '../../common/formik-material-ui';
import { FormikSwitch } from '../../common/formik-material-ui/FormikSwitch';
import '../../common/formik-material-ui/uniqueProperty';
import { loadableEnum } from '../../common/useMakePHPRequest';
import { trackModalUpdates, trackSaveIVR } from '../../common/userTracking';
import { isUrl } from '../../common/validators';
import { Business } from '../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import { IUserAtom, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import {
  createHoursString,
  Scheduler,
} from '../components/CadenceSettings/Scheduler';
import {
  DefaultAction,
  DefaultActionSkeleton,
  getDefaultActionPayload2,
  getDefaultActionUrl,
  IActionType,
} from '../components/dropdowns/DefaultAction';
import TabLabel from '../components/general/TabLabel';
import {
  getErrorTabIndex,
  isErrorInTab,
} from '../components/modals/DevicesAndRoutingModal';
import { ModalWithStyles } from '../components/modals/ModalWithStyles';
import { WizardModal } from '../components/modals/WizardModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { RecordNow } from '../components/recorder/RecordNow';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {},
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  delete: {
    padding: 0, // shrink delete icon on action button
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  fileUploadLabel: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: 'min-content',
  },
  addButton: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  deleteIconButton: {
    color: 'red',
  },
  deleteIconButtonDisabled: {
    color: 'grey',
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiButton-label': {
      padding: 2,
    },
  },
  audio: {
    '&:focus': {
      outline: 0,
    },
  },
  modal: {
    paddingBottom: 0,
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  tabIcon: {
    marginRight: 15,
  },
}));
const tabNames = [
  {
    title: 'Settings',
    icon: SettingsApplicationsIcon,
  },
  {
    title: 'IVR Hours',
    icon: QueryBuilderIcon,
  },
  {
    title: 'Routing',
    icon: NotificationsActiveIcon,
  },
];
export function IvrSettings({ show, onHide, id }) {
  const classes = useStyles();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtom.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const [submitState, setSubmitState] = useState(loadableEnum.EMPTY);
  const [tabIndex, setTabIndex] = useState(0);
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);
  // eslint-disable-next-line
  const handleChange = (formik) => async (event, newIndex) => {
    setTabIndex(+newIndex);
  };
  const getBusinessIvrs = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_IVRS })
  );
  const setRefetchToggleForBusinessIvrs = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_BUSINESS_IVRS)
  );
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const ivrForEdit = useMemo(
    () =>
      getBusinessIvrs.state === 'hasValue' && id
        ? getBusinessIvrs.contents?.data?.result?.find(
            (ivr) => ivr.mainIVRid.toString() === id.toString()
          )
        : {},
    [id, getBusinessIvrs]
  );
  const ivrSettingsRequest = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_IVR_SETTINGS,
      payloadJsonString: JSON.stringify({ ivrid: id }),
      waitForPayload: !id, //todo: we should probably not be making this call
    })
  );
  const setRefetchToggleForIvrSettings = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_IVR_SETTINGS)
  );
  const [uploadedFiles, setUploadedFiles] = useState<any>({});
  useEffect(() => {
    setUploadedFiles({});
  }, [id]);

  const [filenames, setFilenames] = useState({
    voicemail: isUrl(ivrForEdit?.message)
      ? ivrForEdit?.message.split('/').slice(-1).pop()
      : '', // filename from uri/path
    greeting: isUrl(ivrForEdit?.message)
      ? ivrForEdit?.message.split('/').slice(-1).pop()
      : '', // filename from uri/path
  }); // fields cannot be null/undefined
  const [filepaths, setFilepaths] = useState({
    voicemail: isUrl(ivrForEdit?.message) ? ivrForEdit?.message : '',
    greeting: isUrl(ivrForEdit?.message) ? ivrForEdit?.message : '',
  });
  // set initial values and update when new data arrives
  useEffect(() => {
    setFilenames({
      voicemail: isUrl(ivrForEdit?.message)
        ? ivrForEdit?.message.split('/').slice(-1).pop()
        : '', // filename from uri/path
      greeting: isUrl(ivrForEdit?.message)
        ? ivrForEdit?.message.split('/').slice(-1).pop()
        : '', // filename from uri/path
    });
    setFilepaths({
      voicemail: isUrl(ivrForEdit?.message) ? ivrForEdit?.message : '',
      greeting: isUrl(ivrForEdit?.message) ? ivrForEdit?.message : '',
    });
  }, [ivrForEdit, getBusinessIvrs]);

  const onChangeFile = (type) => ({ target: { files } }) => {
    setFilenames({
      ...filenames,
      // voicemail or greeting
      [type]:
        files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? '',
    });
    setFilepaths({
      ...filepaths,
      [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
    });
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    }); // take first file
  };

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business & {
      // eslint-disable-next-line func-call-spacing
      businessHarassmentPolicies: (BusinessHarassmentPolicy & {
        days: number;
        hours: number;
        minutes: number;
      })[];
    }
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtomContents.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business: Business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable]
  );

  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const handleShowScheduler = () => setShowScheduler(true);
  const onHideHours = () => {
    setShowScheduler(false);
  };

  const formikRef = useRef(null);
  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours: any) => {
    schedulableHoursCached.current = hours;
  };
  const themeMode = useRecoilValue(themeModeAtom);

  const handleRecording = (type) => (files) => {
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    });
  };
  const settings = useMemo(
    () =>
      ivrSettingsRequest.state === 'hasValue' &&
      ivrSettingsRequest.contents?.data?.meta?.success
        ? ivrSettingsRequest.contents?.data?.result
        : {},
    [ivrSettingsRequest, id]
  );
  const addnewIvrInitialValues = {
    submitIvrMp3File: {
      files: '',
    },
    submitIVRGreetingMp3File: {
      name: ivrForEdit?.message ?? '',
    },
    mainDefaultActionPayload: {
      type: 'hangup' as IActionType,
      ivr: '',
      group: '',
      user: '',
      queue: '',
      postMessageDefaultAction: '',
    },
    submitIvrPayload: {
      name: '',
      extDialing: false,
      messageType: 'tts',
      message: 'Default Message',
    },
    routing: [
      {
        action: '',
        digit: '1',
        type: '',
        ivr: '',
        group: '',
        user: '',
        queue: '',
      },
    ],
    useBusinessHoursEnabled: true,
    ivrHours: businessHoursMemo,
    closedActionPayload: getDefaultActionPayload2({
      defaultAction: get(settings, `[0].closedAction`),
      id,
    }),
  };
  const ivrSchema = useMemo(
    () =>
      Yup.object({
        submitIvrPayload: Yup.object({
          name: Yup.string().required('Required'),
        }),
        closedActionPayload: Yup.object().shape({
          type: Yup.string().required('required'),
          group: Yup.string().when('type', {
            is: (type) => type === 'group',
            then: Yup.string().required('Select a Group'),
          }),
          ivr: Yup.string().when('type', {
            is: (type) => type === 'ivr',
            then: Yup.string().required('Select a ivr'),
          }),
          queue: Yup.string().when('type', {
            is: (type) => type === 'queue',
            then: Yup.string().required('Select a queue'),
          }),
          user: Yup.string().when('type', {
            is: (type) => type === 'user',
            then: Yup.string().required('Select a user'),
          }),
        }),
        routing: Yup.array().of(
          Yup.object()
            .uniqueProperty('digit', 'Digits must be unique')
            .shape({
              digit: Yup.string()
                .required('Required')
                .matches(/^[0-9]*$/g, 'Digit must be a number between 0-9999')
                .min(0, 'Minimum of 0')
                .max(4, 'Maximum of 4 Characters'),
            })
        ),
        submitIVRGreetingMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
      }),
    []
  );

  const ivrSettingsInitialValuesMemo = useMemo(
    () =>
      ivrSettingsRequest.contents?.data?.meta?.success &&
      getBusinessIvrs.state === 'hasValue'
        ? {
            submitIvrMp3File: {
              files: '',
            },
            submitIVRGreetingMp3File: {
              name: ivrForEdit?.message ?? '',
            },
            mainDefaultActionPayload: getDefaultActionPayload2({
              defaultAction: ivrForEdit?.postMessageDefaultAction,
              id,
            }),
            submitIvrPayload: {
              name: ivrForEdit?.IVRname ?? '',
              extDialing: ivrForEdit?.directoryOn === 1,
              messageType: ivrForEdit?.message?.endsWith('.mp3')
                ? 'mp3'
                : 'tts',
              message: ivrForEdit?.message?.endsWith('.mp3')
                ? '' // do not show mp3 file
                : ivrForEdit?.message,
            },
            routing: (settings ?? []).map((ivr) => ({
              action: ivr.action ?? '',
              digit: ivr.digit === null ? '' : ivr.digit,
              type: ivr.actionType ? ivr.actionType : 'hangup',
              ivr: ivr.actionType === 'ivr' ? ivr.actionValue : '',
              group: ivr.actionType === 'group' ? ivr.actionValue : '',
              user: ivr.actionType === 'user' ? ivr.actionValue : '',
              queue: ivr.actionType === 'queue' ? ivr.actionValue : '',
            })),
            useBusinessHoursEnabled: !!Number(
              get(settings, `[0].useBusinessHoursEnabled`, '1')
            ),
            ivrHours: get(settings, `[0].ivrHours`)
              ? JSON.parse(settings[0].ivrHours)
              : businessHoursMemo,
            closedActionPayload: addnewIvrInitialValues.closedActionPayload,
          }
        : addnewIvrInitialValues,
    [ivrForEdit, id, getBusinessIvrs, ivrSettingsRequest]
  );
  function trackUpdates(values, ivrSettingsInitialValuesMemo, currentTabTitle) {
    trackModalUpdates(
      values,
      ivrSettingsInitialValuesMemo,
      'IVR Settings Updated',
      currentTabTitle
    );
  }
  const tabToFields = [
    ['submitIvrPayload', 'mainDefaultActionPayload'],
    ['ivrHours', 'closedActionPayload'],
    ['routing'],
  ];
  return (
    <Paper elevation={0} square={true}>
      <Formik
        enableReinitialize={true}
        validationSchema={ivrSchema}
        onSubmit={async (values, actions) => {
          trackUpdates(values, ivrSettingsInitialValuesMemo, currentTabTitle);
          setSubmitState(loadableEnum.LOADING);
          /// TO DO: INSERT LOGIC FOR BATCHING REQUEST ON ADD IVR
          //if id is undefined then submit the request for adding an ivr then extract the id and set it to a variable
          async function submitNewIvr() {
            const payloadToSend = {
              call: methodNameEnum.SUBMIT_NEW_IVR,
              apikey: userAtomContents?.apikey,
              businessid: userAtomContents?.businessid,
              ivrname: values.submitIvrPayload.name,
            };
            const response = await mutation.mutateAsync({
              endpoint: endpointEnum.SUBMIT_NEW_IVR,
              payload: payloadToSend,
            });
            const resSuccess = response?.data?.success;
            const resMessage = response?.data?.result?.code;

            if (!resSuccess) {
              enqueueSnackbarWithStyles({
                message:
                  resMessage === 'ER_DUP_ENTRY'
                    ? 'Please choose an IVR Name that is not already in use'
                    : 'Fail to add new IVR',
                alertProps: { severity: 'error' },
              });
              return;
            }

            return response?.data?.result?.[0]?.ivrid;
          }
          const ivrId = id || (await submitNewIvr());
          if (!ivrId) {
            setSubmitState(loadableEnum.CANCELED);
            return;
          }
          const payload = {
            IVR: {
              general: {
                name: values.submitIvrPayload.name,
                greetingType: values.submitIvrPayload.messageType,
                greeting:
                  values.submitIvrPayload.messageType === 'mp3' ||
                  values.submitIvrPayload.messageType === 'record'
                    ? ''
                    : values.submitIvrPayload.message,

                defaultAction: getDefaultActionUrl({
                  ...values.mainDefaultActionPayload,
                  businessid: userAtomContents?.businessid,
                  parentType: 'ivr',
                  voicemail: ivrId,
                }),
                closedAction: getDefaultActionUrl({
                  ...values.closedActionPayload,
                  businessid: userAtomContents?.businessid,
                  parentType: 'ivr',
                  voicemail: ivrId,
                }),
                useBusinessHoursEnabled: values.useBusinessHoursEnabled,
                ivrHours: values.ivrHours,
              },
              tiers: values.routing.reduce(function (acc, curr, index) {
                // acc is accumulator which is the required object.
                // this will create a nee key in accumulator and will set its value
                acc[index] = {
                  digit: curr.digit,
                  actiontype: curr.type,
                  actionid: curr[curr.type],
                  action: getDefaultActionUrl({
                    type: curr.type,
                    parentType: 'ivr',
                    businessid: userAtomContents.businessid,
                    voicemail: curr.ivr,
                    group: curr.group,
                    ivr: curr.ivr,
                    queue: curr.queue,
                    user: curr.user,
                  }),
                };
                return acc;
              }, {}),
            },
            ivrid: id,
          };
          const data = new FormData();
          data.append('call', 'submitUpdateIVR');
          data.append('IVR', JSON.stringify(payload.IVR));
          data.append('ivrid', ivrId);
          data.append('businessid', String(userAtomContents.businessid));
          data.append('apiKey', userAtomContents.apikey);
          const requestConfig: AxiosRequestConfig = {
            method: 'post',
            url: `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
            headers: {
              'content-type': 'multipart/form-data',
            },
            withCredentials: true,
            data,
          };

          // make ajax request
          const _result = await Axios(requestConfig);
          const dupNameError = _result?.data?.code === 1062; // 1062 - duplicate key violation
          if (dupNameError) {
            enqueueSnackbarWithStyles({
              message: 'Please choose an IVR Name that is not already in use',
              alertProps: { severity: 'error' },
            });
            setSubmitState(loadableEnum.HAS_ERROR);
            return;
          }
          //   console.log(_result, 'result updating ');
          const updateIvrExtensionDialingToggle = async () => {
            const data = {
              call: methodNameEnum.UPDATE_IVR_EXTENSION_DIALING,
              apikey: userAtomContents?.apikey,
              businessid: userAtomContents.businessid,
              ivrid: ivrId,
              name: 'directoryOn',
              value: values.submitIvrPayload.extDialing ? 1 : 0,
            };
            await mutation.mutateAsync({
              endpoint: endpointEnum.UPDATE_IVR_EXTENSION_DIALING,
              payload: data,
            });
          };
          const sameValueBoolean =
            (ivrForEdit?.directoryOn === 1) ===
            values.submitIvrPayload.extDialing;
          if (!sameValueBoolean) {
            updateIvrExtensionDialingToggle();
          }
          if (uploadedFiles?.greeting) {
            if (
              uploadedFiles?.greeting[0]?.size > 1 &&
              values.submitIvrPayload.messageType === 'mp3'
            ) {
              const mp3Data = new FormData();
              mp3Data.append('call', 'submitIVRMp3File');
              mp3Data.append('ivrid', ivrId);
              mp3Data.append('businessid', String(userAtomContents.businessid));
              mp3Data.append('apiKey', userAtomContents.apikey);
              mp3Data.append('mp3', uploadedFiles?.greeting[0]);
              const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
              await Axios.post(url, mp3Data, { withCredentials: true });
            }
            if (
              uploadedFiles?.greeting[0]?.size > 1 &&
              values.submitIvrPayload.messageType === 'record'
            ) {
              const formData = new FormData();
              // ***all params are required****
              formData.append('webm', uploadedFiles.greeting[0]);
              formData.append(
                'businessid',
                String(userAtomContents?.businessid)
              );
              formData.append('apikey', userAtomContents?.apikey);
              formData.append('userid', ivrId); // user id of agent being changed
              formData.append('id', ivrId); // user id of agent being changed
              formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
              formData.append('iid', '0'); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
              formData.append('name', values.submitIvrPayload.name); // for vm drop name MUST be name of voicemail drop
              formData.append('type', 'ivr'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
              const _response3 = await Axios.post(
                `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
                formData
              );
            }
          }
          setRefetchToggleForBusinessIvrs({});
          setRefetchToggleForIvrSettings({});
          if (!id) {
            enqueueSnackbarWithStyles({
              message: `Successfully Added ${values.submitIvrPayload.name} IVR !`,
              alertProps: { severity: 'success' },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: `Successfully Updated ${values.submitIvrPayload.name} IVR Settings!`,
              alertProps: { severity: 'success' },
            });
          }
          setSubmitState(loadableEnum.HAS_VALUE);
          onHide();
        }}
        initialValues={
          id ? ivrSettingsInitialValuesMemo : addnewIvrInitialValues
        }
        innerRef={formikRef}
        // validateOnChange={false}
        // validateOnBlur={false}
      >
        {(formik) => {
          function handleSubmit() {
            if (!createHoursString(schedulableHoursCached.current)) {
              enqueueSnackbarWithStyles({
                message: 'Please select a time range',
                alertProps: {
                  severity: 'warning',
                },
              });
            } else {
              formik.setFieldValue('ivrHours', schedulableHoursCached.current);
              onHideHours();
            }
          }

          const ivrHoursString = useMemo<string[]>(
            () =>
              createHoursString(
                formik.values.useBusinessHoursEnabled
                  ? businessCached.current?.businessDetails?.businessHours
                  : formik.values.ivrHours
              ).split(','),
            [
              showBusinessHours,
              setShowBusinessHours,
              formik.values.useBusinessHoursEnabled,
              formik.values.ivrHours,
              businessLoadable,
            ]
          );
          // useEffect(() => {
          //   for (let i = 0; i < formik.values.routing.length; i++) {
          //     if (formik.values.routing[i].type.length > 1) {
          //       const defaultActionUrl = getDefaultActionUrl({
          //         ...formik.values.routing[i],
          //         businessid: userAtomContents?.businessid,
          //         parentType: 'ivr',
          //       });
          //       formik.setFieldValue(`routing.${i}.action`, defaultActionUrl);
          //     }
          //   }
          // }, [formik.values.routing]);
          // useEffect(() => {
          //   if (formik.values.mainDefaultActionPayload.type.length > 1) {
          //     const defaultActionUrl = getDefaultActionUrl({
          //       ...formik.values.mainDefaultActionPayload,
          //       businessid: userAtomContents?.businessid,
          //       parentType: 'ivr',
          //     });
          //     formik.setFieldValue(
          //       'mainDefaultActionPayload.postMessageDefaultAction',
          //       defaultActionUrl
          //     );
          //   }
          // }, []);
          return (
            <TabContext value={String(tabIndex)}>
              <WizardModal
                title={tabNames[tabIndex].title}
                disableSubmit={!formik.isValid}
                isLoading={
                  submitState === loadableEnum.LOADING ||
                  (ivrSettingsRequest.state === 'loading' && !!id)
                }
                navigation={
                  <>
                    <Tab
                      label={id ? 'Edit IVR' : 'Add New IVR'}
                      disabled={true}
                      className={clsx(classes.tab)}
                    />
                    <TabList
                      onChange={handleChange(formik)}
                      aria-label={id ? 'Edit IVR' : 'Add New IVR'}
                      orientation="vertical"
                      variant="standard"
                      className={clsx(classes.tabs, {})}
                    >
                      {tabNames.map((tabItem, index) => (
                        <Tab
                          {...{ name: tabItem.title }}
                          key={index}
                          className={classes.tab}
                          label={
                            <TabLabel
                              index={index}
                              formik={formik}
                              tabToFields={tabToFields}
                              tabItem={tabItem}
                            />
                          }
                          value={String(index)}
                        />
                      ))}
                    </TabList>
                  </>
                }
                dialogActions={
                  <>
                    <Button
                      className={classes.button}
                      variant="contained"
                      color="inherit"
                      disabled={submitState === loadableEnum.LOADING}
                      onClick={() => {
                        onHide();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className={classes.button}
                      variant="contained"
                      color="primary"
                      disabled={formik.isSubmitting || !formik.dirty}
                      onClick={(event) => {
                        // trigger show errors if errors exists
                        formik.handleSubmit(event as any);

                        // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                        if (
                          !formik.isValid && // if form invalid
                          !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        ) {
                          const errorTabIndex = getErrorTabIndex(
                            formik.errors,
                            tabToFields
                          );
                          /**
                           * switch to tab containing error
                           *
                           * since form validation is in flight and makes page rendering updates
                           * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                           * putting state changes in a separate tick will prevent coliding page rendering updates
                           *
                           * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                           */
                          setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        }
                      }}
                    >
                      Save
                    </Button>
                  </>
                }
                size="md"
                show={show}
                onHide={onHide}
                onSubmit={() => {
                  trackSaveIVR();
                  formik.handleSubmit();
                }}
              >
                <TabPanel value="0">
                  <FormikTextField
                    name="submitIvrPayload.name"
                    className={classes.field}
                    formik={formik}
                    fullWidth
                    label="IVR Name"
                    inputProps={{ maxLength: 45 }}
                    size="small"
                    required
                    variant="outlined"
                  />
                  <RecordNow
                    dropdownLabel="IVR Greeting Type"
                    dropdownName="submitIvrPayload.messageType"
                    dropdownType={
                      formik.values.submitIvrPayload?.messageType as any
                    }
                    outlineLabel="IVR Greeting Type"
                    ttsLabel="IVR Greeting"
                    ttsName="submitIvrPayload.message"
                    filenameName="submitIvrMp3File.files"
                    filesizeName="submitIVRGreetingMp3File.name"
                    formik={formik}
                    filename={filenames.greeting}
                    filepath={filepaths.greeting}
                    onChangeFile={onChangeFile('greeting')}
                    onRecording={handleRecording('greeting')}
                  >
                    <MenuItem value="tts">Text-to-Speech</MenuItem>
                    <MenuItem value="mp3">MP3</MenuItem>
                    <MenuItem value="record">Record Now</MenuItem>
                  </RecordNow>
                  <FormControlLabel
                    control={
                      <FormikSwitch
                        name="submitIvrPayload.extDialing"
                        formik={formik}
                        color="primary"
                        type="checkbox"
                      />
                    }
                    label="Extension Dialing"
                  />
                  <Suspense fallback={<DefaultActionSkeleton />}>
                    <DefaultAction
                      defaultAction={getDefaultActionUrl({
                        ...formik.values.mainDefaultActionPayload,
                        parentType: 'ivr',
                        businessid: userAtomContents.businessid,
                        voicemail: '',
                      })}
                      actionType={
                        formik?.values?.mainDefaultActionPayload?.type
                      }
                      typeName="mainDefaultActionPayload.type"
                      ivrName="mainDefaultActionPayload.ivr"
                      groupName="mainDefaultActionPayload.group"
                      userName="mainDefaultActionPayload.user"
                      queueName="mainDefaultActionPayload.queue"
                      formik={formik}
                      showVoicemail={false}
                      id={id}
                    />
                  </Suspense>
                </TabPanel>
                <TabPanel value="1">
                  <div style={{ marginTop: '16px' }}>
                    <LabelledOutline
                      label="IVR Hours"
                      backgroundColor={
                        themeMode === themeModeEnum.DARK_MODE
                          ? '#121c21'
                          : '#fff'
                      }
                    >
                      <div className="row">
                        <div className="col-lg-6">
                          Actions will be routed:
                          {ivrHoursString.map((string, index) => (
                            <div key={index}>
                              {string} <br />
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            // paddingRight: '50px',
                            alignItems: 'center',
                          }}
                          className="col-lg-6"
                        >
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="useBusinessHoursEnabled"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Use Business Hours"
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.editButton}
                            onClick={handleShowScheduler}
                            disabled={formik.values.useBusinessHoursEnabled}
                          >
                            Edit IVR Hours
                          </Button>
                        </div>
                      </div>
                    </LabelledOutline>
                    <Suspense fallback={<DefaultActionSkeleton />}>
                      <DefaultAction
                        defaultAction={getDefaultActionUrl({
                          type: formik.values.closedActionPayload?.type,
                          parentType: 'ivr',
                          businessid: userAtomContents.businessid,
                          user: formik.values.closedActionPayload?.user,
                          group: formik.values.closedActionPayload?.group,
                          ivr: formik.values.closedActionPayload?.ivr,
                          queue: formik.values.closedActionPayload?.queue,
                          voicemail: '',
                        })}
                        ignoreDefaultAutocomplete={true}
                        formikField="closedActionPayload"
                        label="Outside IVR Hours Action"
                        labelForBusiness="Outside Business Hours Action"
                        actionType={formik.values.closedActionPayload?.type}
                        typeName="closedActionPayload.type"
                        ivrName="closedActionPayload.ivr"
                        groupName="closedActionPayload.group"
                        userName="closedActionPayload.user"
                        queueName="closedActionPayload.queue"
                        formik={formik}
                        disableFormUpdate={true}
                        showVoicemail={false}
                        id={id}
                      />
                    </Suspense>
                  </div>
                  <ModalWithStyles
                    onSubmit={handleSubmit}
                    title={'Business Hours'}
                    show={showScheduler}
                    onHide={onHideHours}
                    isSubmitting={formik.isSubmitting}
                    size="lg"
                  >
                    <Scheduler
                      schedulableHours={formik.values.ivrHours}
                      setSchedulableHours={handleSetSchedulableHours}
                    />
                  </ModalWithStyles>
                </TabPanel>
                <TabPanel value="2">
                  <Form>
                    <FieldArray
                      name="routing"
                      render={(arrayHelpers) =>
                        formik.values.routing.map((setting, index) => (
                          <div key={index} className="row">
                            <div className="col-md-auto">
                              <FormikTextField
                                name={`routing[${index}].digit`}
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                helperText="Digit For Ivr Action (0-9999)"
                                label="Digit"
                                size="small"
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 4,
                                  },
                                }}
                                required
                                //type="number"
                                variant="outlined"
                              />
                            </div>
                            <div className="col">
                              <Suspense fallback={<DefaultActionSkeleton />}>
                                <DefaultAction
                                  defaultAction={getDefaultActionUrl({
                                    ...formik.values.routing[index],
                                    parentType: 'ivr',
                                    businessid: userAtomContents.businessid,
                                    voicemail: '',
                                  })}
                                  actionType={
                                    formik?.values?.routing[index]?.type
                                  }
                                  typeName={`routing[${index}].type`}
                                  ivrName={`routing[${index}].ivr`}
                                  groupName={`routing[${index}].group`}
                                  userName={`routing[${index}].user`}
                                  queueName={`routing[${index}].queue`}
                                  formik={formik}
                                  showVoicemail={false}
                                  label={'Action'}
                                  id={id}
                                />
                              </Suspense>
                            </div>
                            <div className="col col-lg-2">
                              <Tooltip title="add">
                                <>
                                  <IconButton
                                    aria-label="add"
                                    color="primary"
                                    onClick={() => {
                                      arrayHelpers.insert(index + 1, {
                                        action: '',
                                        digit: '',
                                        type: '',
                                        ivr: '',
                                        group: '',
                                        user: '',
                                        queue: '',
                                      });
                                    }}
                                    size="large"
                                  >
                                    <PlaylistAddIcon />
                                  </IconButton>
                                </>
                              </Tooltip>
                              <IconButton
                                aria-label="delete"
                                disabled={formik.values.routing.length < 2}
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                }}
                                size="large"
                              >
                                <DeleteIcon
                                  className={
                                    formik.values.routing.length < 2
                                      ? classes.deleteIconButtonDisabled
                                      : classes.deleteIconButton
                                  }
                                />
                              </IconButton>
                            </div>
                          </div>
                        ))
                      }
                    />
                  </Form>
                </TabPanel>
              </WizardModal>
            </TabContext>
          );
        }}
      </Formik>
    </Paper>
  );
}
